import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { sesionCajaService, SesionCajaCreate } from '../../services/sesionCajaService';
import { cajaService } from '../../services/cajaService';
import { useAuthStore } from '../../store/authStore';

interface AbrirSesionFormProps {
  onClose: () => void;
}

const AbrirSesionForm = ({ onClose }: AbrirSesionFormProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: cajasData } = useQuery(
    'cajas',
    () => cajaService.getAll(1, 100)
  );

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<SesionCajaCreate>({
    defaultValues: {
      montoApertura: 0
    }
  });

  const abrirMutation = useMutation(sesionCajaService.abrir, {
    onSuccess: () => {
      toast.success('Sesión abierta correctamente');
      queryClient.invalidateQueries(['sesiones-caja']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al abrir sesión');
    }
  });

  const onSubmit = (data: SesionCajaCreate) => {
    abrirMutation.mutate(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Abrir Sesión de Caja</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Caja *
            </label>
            <select
              {...register('cajaId', { 
                required: 'Caja es requerida',
                valueAsNumber: true
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={0}>Selecciona una caja</option>
              {cajasData?.data.cajas.map((caja) => (
                <option key={caja.id} value={caja.id}>
                  {caja.name} - Sucursal: {caja.sucursal?.name || '-'}
                </option>
              ))}
            </select>
            {errors.cajaId && (
              <p className="text-red-500 text-sm mt-1">{errors.cajaId.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto de Apertura
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              {...register('montoApertura', { 
                valueAsNumber: true,
                min: { value: 0, message: 'Debe ser mayor o igual a 0' }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="0.00"
            />
            {errors.montoApertura && (
              <p className="text-red-500 text-sm mt-1">{errors.montoApertura.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              {...register('notas')}
              rows={3}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Notas sobre la sesión..."
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={abrirMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {abrirMutation.isLoading ? 'Abriendo...' : 'Abrir Sesión'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AbrirSesionForm;

